import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Archive, ArchiveRestore } from "lucide-react";
import EditModal from "@/components/site/EditModal";

const CONTENT_TYPES = [
  { id: "testimonials", label: "Témoignages" },
  { id: "actions", label: "Actions (agenda)" },
  { id: "pedago", label: "Pédago" },
  { id: "initiatives", label: "Initiatives (carte)" },
];

const ACTION_STATUS_OPTIONS = ["a_venir", "en_cours", "termine"];
const ACTION_TYPE_OPTIONS = ["evenement", "mobilisation", "outil"];
const PEDAGO_FORMATS = ["article", "faq", "infographie", "video"];
const TESTIMONIAL_FORMATS = ["article", "video", "podcast", "anonyme"];
const INIT_TYPES = ["conseil_eleves", "action", "campagne", "groupe"];
const INIT_PROVINCES = ["Bruxelles", "Liège", "Hainaut", "Namur", "Brabant wallon", "Luxembourg"];

const ENDPOINTS = {
  testimonials: { list: "/admin/testimonials", patch: "/admin/testimonials", del: "/admin/testimonials" },
  actions: { list: "/admin/actions", patch: "/admin/actions", del: "/admin/actions", create: "/actions" },
  pedago: { list: "/admin/pedago", patch: "/admin/pedago", del: "/admin/pedago", create: "/admin/pedago" },
  resources: { list: "/admin/resources", patch: "/admin/resources", del: "/admin/resources", create: "/admin/resources" },
  initiatives: { list: "/admin/initiatives", patch: "/admin/initiatives", del: "/initiatives" },
};

export default function ContenusTab() {
  const [sub, setSub] = React.useState("testimonials");
  const [showArchived, setShowArchived] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const load = React.useCallback(() => {
    api.get(ENDPOINTS[sub].list).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [sub]);
  React.useEffect(() => { load(); }, [load]);

  const filtered = React.useMemo(() => items.filter((it) => showArchived || !it.archived), [items, showArchived]);

  const toggleArchive = async (it) => {
    try {
      await api.patch(`${ENDPOINTS[sub].patch}/${it.id}`, { archived: !it.archived });
      toast.success(it.archived ? "Restauré" : "Archivé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT ? (préfère 'Archiver')")) return;
    try {
      await api.delete(`${ENDPOINTS[sub].del}/${id}`);
      toast.success("Supprimé"); load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const saveEdit = async (vals) => {
    try {
      const payload = { ...vals };
      if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
      if (typeof payload.age === "string" && payload.age !== "") payload.age = parseInt(payload.age) || null;
      await api.patch(`${ENDPOINTS[sub].patch}/${editing.id}`, payload);
      toast.success("Mis à jour");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); throw err; }
  };

  const createNew = async (vals) => {
    try {
      const payload = { ...vals };
      if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post(ENDPOINTS[sub].create, payload);
      toast.success("Créé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); throw err; }
  };

  const canCreate = !!ENDPOINTS[sub].create;

  return (
    <div data-testid="contenus-tab">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        {CONTENT_TYPES.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)} className={`chip ${sub === t.id ? "bg-black text-white" : "bg-white"}`} data-testid={`contenus-sub-${t.id}`}>{t.label}</button>
        ))}
        <label className="ml-auto chip bg-white inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="toggle-archived-contenus" /> Voir archivés
        </label>
        {canCreate && (
          <button onClick={() => setCreating(true)} className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid="contenus-new"><Plus className="w-4 h-4" /> Nouveau</button>
        )}
      </div>

      <div className="grid gap-3" data-testid={`contenus-list-${sub}`}>
        {filtered.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500">Aucun élément.</div>}
        {filtered.map((it) => (
          <ContenuRow key={it.id} sub={sub} item={it} onEdit={() => setEditing(it)} onArchive={() => toggleArchive(it)} onDelete={() => del(it.id)} />
        ))}
      </div>

      {editing && <EditModal title={`Éditer · ${sub}`} initial={normalizeForEdit(sub, editing)} fields={fieldsFor(sub)} onClose={() => setEditing(null)} onSave={saveEdit} />}
      {creating && <EditModal title={`Nouveau · ${sub}`} initial={defaultFor(sub)} fields={fieldsFor(sub).filter((f) => f.key !== "archived")} onClose={() => setCreating(false)} onSave={(v) => { setCreating(false); return createNew(v); }} />}
    </div>
  );
}

function ContenuRow({ sub, item, onEdit, onArchive, onDelete }) {
  const archived = !!item.archived;
  return (
    <div className={`neo-card p-4 flex items-start justify-between gap-3 flex-wrap ${archived ? "opacity-60" : ""}`} data-testid={`contenu-${item.id}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="font-bold flex items-center gap-2">
          {archived && <span className="chip bg-neutral-300 text-xs">📦 archivé</span>}
          <ContenuTitle sub={sub} item={item} />
        </div>
        <div className="text-xs text-neutral-500 mt-1"><ContenuSubtitle sub={sub} item={item} /></div>
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`edit-contenu-${item.id}`}><Edit3 className="w-4 h-4" /> Éditer</button>
        <button onClick={onArchive} className={`neo-btn text-sm py-2 ${archived ? "bg-cef-green" : "bg-white"}`} data-testid={`archive-contenu-${item.id}`}>
          {archived ? <><ArchiveRestore className="w-4 h-4" /> Restaurer</> : <><Archive className="w-4 h-4" /> Archiver</>}
        </button>
        <button onClick={onDelete} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`del-contenu-${item.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function ContenuTitle({ sub, item }) {
  if (sub === "testimonials") return <>« {(item.text || "").slice(0, 80)}{(item.text || "").length > 80 ? "…" : ""} »</>;
  if (sub === "initiatives") return item.name;
  return item.title;
}

function ContenuSubtitle({ sub, item }) {
  if (sub === "testimonials") return <>{item.format} · {item.author || "Anonyme"} {item.theme ? `· #${item.theme}` : ""} {item.approved ? "" : "· en modération"}</>;
  if (sub === "actions") return <>{item.type} · {item.status} · {item.date} · {item.location}</>;
  if (sub === "pedago") return <>{item.format} {item.tags?.length ? `· #${item.tags.join(", #")}` : ""}</>;
  if (sub === "resources") return <>{item.category || "—"} {item.link ? `· ${item.link}` : ""}</>;
  if (sub === "initiatives") return <>{item.type} · {item.school} · {item.city} · {item.province}{item.date ? ` · ${item.date.slice(0, 10)}` : ""}</>;
  return null;
}

function normalizeForEdit(sub, item) {
  if (sub === "pedago" || sub === "testimonials") {
    return { ...item, tags: Array.isArray(item.tags) ? item.tags.join(", ") : (item.tags || "") };
  }
  return { ...item };
}

function defaultFor(sub) {
  if (sub === "pedago") return { title: "", summary: "", content: "", format: "article", section: "reformes", media_url: "", tags: "" };
  if (sub === "resources") return { title: "", content: "", category: "", link: "" };
  if (sub === "actions") return { title: "", date: "", location: "", description: "", content: "", image_url: "", kit_url: "", status: "a_venir", type: "evenement" };
  return {};
}

function fieldsFor(sub) {
  if (sub === "testimonials") return [
    { key: "author", label: "Auteur·rice" },
    { key: "age", label: "Âge", type: "number" },
    { key: "school", label: "École" },
    { key: "province", label: "Province", type: "select", options: INIT_PROVINCES },
    { key: "format", label: "Format", type: "select", options: TESTIMONIAL_FORMATS, required: true },
    { key: "media_url", label: "Media URL" },
    { key: "theme", label: "Thème" },
    { key: "text", label: "Texte", multiline: true, span: 2, required: true },
    { key: "approved", label: "Approuvé (public)", type: "checkbox" },
    { key: "archived", label: "Archivé", type: "checkbox" },
  ];
  if (sub === "actions") return [
    { key: "title", label: "Titre", required: true, span: 2 },
    { key: "date", label: "Date (ISO)", required: true },
    { key: "location", label: "Lieu" },
    { key: "status", label: "Statut", type: "select", options: ACTION_STATUS_OPTIONS, required: true },
    { key: "type", label: "Type", type: "select", options: ACTION_TYPE_OPTIONS, required: true },
    { key: "kit_url", label: "URL pack action", span: 2 },
    { key: "image_url", label: "Image (visuel)", type: "image" },
    { key: "description", label: "Description courte", multiline: true, span: 2, required: true, hint: "Résumé court affiché sur les cartes (1-2 phrases)." },
    { key: "content", label: "Contenu détaillé (page action)", type: "richtext", hint: "Texte long affiché sur la page de détail de l'action." },
    { key: "archived", label: "Archivé", type: "checkbox" },
  ];
  if (sub === "pedago") return [
    { key: "title", label: "Titre", required: true, span: 2 },
    { key: "section", label: "Section (où apparaît cet article)", type: "select", options: [{ value: "reformes", label: "Décoder les réformes" }, { value: "droits", label: "Connaître tes droits" }], required: true, hint: "Choisis dans quel onglet de la page Comprendre l'article sera affiché." },
    { key: "format", label: "Format", type: "select", options: PEDAGO_FORMATS, required: true },
    { key: "media_url", label: "Media URL (vidéo/PDF)" },
    { key: "summary", label: "Résumé", multiline: true, span: 2, required: true },
    { key: "content", label: "Contenu (mise en forme)", type: "richtext", required: true },
    { key: "tags", label: "Tags (séparés par ,)", span: 2 },
    { key: "archived", label: "Archivé", type: "checkbox" },
  ];
  if (sub === "resources") return [
    { key: "title", label: "Titre", required: true, span: 2 },
    { key: "category", label: "Catégorie" },
    { key: "link", label: "Lien externe" },
    { key: "content", label: "Contenu (mise en forme)", type: "richtext", required: true },
    { key: "archived", label: "Archivé", type: "checkbox" },
  ];
  if (sub === "initiatives") return [
    { key: "name", label: "Nom", required: true, span: 2 },
    { key: "type", label: "Type", type: "select", options: INIT_TYPES, required: true },
    { key: "school", label: "École", required: true },
    { key: "city", label: "Commune", required: true },
    { key: "province", label: "Province", type: "select", options: INIT_PROVINCES, required: true },
    { key: "address", label: "Adresse (rue, n°)" },
    { key: "date", label: "Date (ISO)" },
    { key: "contact", label: "Contact" },
    { key: "description", label: "Description", multiline: true, span: 2, required: true },
    { key: "approved", label: "Approuvé (public)", type: "checkbox" },
    { key: "archived", label: "Archivé", type: "checkbox" },
  ];
  return [];
}
